import { HttpParams } from '@angular/common/http';
import { Component, ElementRef, Inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { Client } from '../../../../../projects/kiene-core/src/lib/components/client/classes/client';
import { LocalStorageService } from '../../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { KieneBackendApiService } from '../../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ChartType } from 'angular-google-charts';
import { LetzteAktionenPanelComponent } from '../letzte-aktionen/letzte-aktionen-panel.component';
import { LetzteAktionenTierlisteMenuComponent } from '../letzte-aktionen/letzte-aktionen-tierliste-menu.component';
import { GruppenDashboardImportRefreshService } from '../gruppen-dashboard-import-refresh.service';
import {
    getTierlisteZielbetriebAnzeigeName,
    shouldShowImportTierlisteZielRoute,
} from '../../../../../projects/kiene-core/src/lib/components/gruppe-tier/tierliste-zielbetrieb-display.util';

const PERMISSION_IMPORTIEREN = 9322;

interface DashboardPanelState {
    loading: boolean;
    error: string | null;
}

interface DashboardTierlisteItem {
    tierliste_id: number;
    datum_abgang: string | Date;
    erstellt?: string | Date | null;
    editiert?: string | Date | null;
    gruppe_abgang: string;
    kunden_abgang: string;
    kunde_einstallen?: string;
    kunde_zielbetrieb?: string;
    anzahl_tiere: number;
    status_id: number;
    status: string;
}

type DashboardChartGrouping = 'day' | 'week' | 'month';
type DashboardSeriesKey = 'eingestallte' | 'ausgestallte' | 'tot';
type DashboardPriceSeriesKey = 'einstallpreis' | 'abgangspreis' | 'einstallmwst' | 'abgangmwst';
type GroupAgeUnit = 'weeks' | 'days';
type PriceDateFilter = 'month' | '90days';
type WeightDateFilter = 'month' | '90days' | 'all';

interface DashboardSeriesEvent {
    date: Date | null;
    value: number;
}

interface DashboardPriceByDateEvent {
    date: Date | null;
    gruppeId: number;
    gruppeName: string;
    einstallpreis: number;
    abgangspreis: number;
    einstallmwst: number;
    abgangmwst: number;
}

interface DashboardPriceGroupAggregate {
    gruppeId: number;
    gruppeName: string;
    einstallpreis: number;
    abgangspreis: number;
    einstallmwst: number;
    abgangmwst: number;
}

interface DashboardWeightTimelineRecord {
    gruppeId: number;
    gruppeName: string;
    standortName: string;
    datumVon: string;
    datumBis: string;
    startDate: Date;
    endDate: Date;
    startWeight: number;
    endWeight: number;
}

@Component({
    selector: 'kiene-gruppen-dashboard',
    templateUrl: './gruppen-dashboard.component.html',
    styleUrls: ['./gruppen-dashboard.component.scss'],
})
export class GruppenDashboardComponent implements OnInit, OnDestroy {
    @ViewChild('chartsTabContent') chartsTabContentRef?: ElementRef<HTMLElement>;
    @ViewChild(LetzteAktionenPanelComponent) letzteAktionenPanel?: LetzteAktionenPanelComponent;
    @ViewChildren(LetzteAktionenTierlisteMenuComponent)
    importTierlisteMenus?: QueryList<LetzteAktionenTierlisteMenuComponent>;

    watchClientSubscription: Subscription;
    private importRefreshSubscription?: Subscription;
    private routeSubscription?: Subscription;
    private pendingTierlisteIdFromRoute: number | null = null;
    private openedTierlisteIdFromRoute: number | null = null;
    currentClient: Client;
    isChartsTabActive = false;
    private chartsResizeObserver: ResizeObserver | null = null;

    importTierlisten: DashboardTierlisteItem[] = [];
    importTierlistenExpanded = false;
    importTierlistenHasMore = false;

    importTierlistenState: DashboardPanelState = { loading: false, error: null };

    readonly previewLimit = 5;
    readonly chartWindowSize = 12;
    readonly priceChartMaxElements = 7;
    readonly chartType = ChartType.LineChart;

    chartGrouping: DashboardChartGrouping = 'week';
    chartColumns = ['Zeitraum', 'Eingestallte Tiere', 'Ausgestallte Tiere', 'Tot Tiere'];
    chartData: (string | number | null)[][] = [];
    chartHasData = false;
    chartSeriesMeta: { key: DashboardSeriesKey; label: string; color: string }[] = [
        { key: 'eingestallte', label: 'Eingestallte Tiere', color: '#2f80ed' },
        { key: 'ausgestallte', label: 'Ausgestallte Tiere', color: '#27ae60' },
        { key: 'tot', label: 'Tot Tiere', color: '#f2994a' },
    ];
    chartSeriesVisibility: Record<DashboardSeriesKey, boolean> = {
        eingestallte: true,
        ausgestallte: true,
        tot: true,
    };
    chartOptions: any = {
        legend: { position: 'none' },
        tooltip: { trigger: 'focus' },
        focusTarget: 'category',
        curveType: 'function',
        height: 390,
        chartArea: { left: 96, top: 32, width: '78%', height: '68%' },
        hAxis: {
            slantedText: false,
            textStyle: { fontSize: 10, color: '#6b7280' },
            gridlines: { color: '#d1d5db', count: 12 },
            minorGridlines: { color: '#e5e7eb' },
        },
        vAxis: {
            minValue: 0,
            format: '#,###',
            viewWindowMode: 'pretty',
            viewWindow: { min: 0 },
            textStyle: { fontSize: 10, color: '#6b7280' },
            textPosition: 'out',
            baselineColor: '#9ca3af',
            gridlines: { color: '#e5e7eb', count: 6 },
            minorGridlines: { color: '#f3f4f6' },
        },
        pointSize: 4,
        lineWidth: 3,
        colors: ['#2f80ed', '#27ae60', '#f2994a'],
        backgroundColor: 'transparent',
    };
    readonly priceChartType = ChartType.ColumnChart;
    priceChartColumns: any[] = ['Datum', 'Einstallpreis', 'Abgangspreis', 'MwSt Einstallpreis', 'MwSt Abgangspreis'];
    priceChartData: (string | number | null)[][] = [];
    priceChartHasData = false;
    priceChartSeriesMeta: { key: DashboardPriceSeriesKey; label: string; color: string }[] = [
        { key: 'einstallpreis', label: 'Einstallpreis', color: '#1d4ed8' },
        { key: 'abgangspreis', label: 'Abgangspreis', color: '#15803d' },
        { key: 'einstallmwst', label: 'MwSt Einstallpreis', color: '#8b5cf6' },
        { key: 'abgangmwst', label: 'MwSt Abgangspreis', color: '#c4b5fd' },
    ];
    priceChartSeriesVisibility: Record<DashboardPriceSeriesKey, boolean> = {
        einstallpreis: true,
        abgangspreis: true,
        einstallmwst: true,
        abgangmwst: true,
    };
    priceDateFilter: PriceDateFilter = 'month';
    weightDateFilter: WeightDateFilter = 'month';
    priceChartOptions: any = {
        legend: { position: 'none' },
        tooltip: { trigger: 'focus' },
        focusTarget: 'datum',
        height: 390,
        chartArea: { left: 96, top: 32, width: '76%', height: '68%' },
        hAxis: {
            slantedText: false,
            textStyle: { fontSize: 10, color: '#6b7280' },
            gridlines: { color: '#d1d5db', count: 12 },
            minorGridlines: { color: '#e5e7eb' },
        },
        vAxis: {
            format: '#,##0.00',
            viewWindowMode: 'pretty',
            textStyle: { fontSize: 10, color: '#6b7280' },
            textPosition: 'out',
            baselineColor: '#9ca3af',
            gridlines: { color: '#e5e7eb', count: 6 },
            minorGridlines: { color: '#f3f4f6' },
        },
        bar: { groupWidth: '72%' },
        colors: ['#1d4ed8', '#15803d', '#8b5cf6', '#c4b5fd'],
        backgroundColor: 'transparent',
    };
    readonly groupAgeChartType = ChartType.BarChart;
    groupAgeChartColumns: string[] = ['Gruppe', 'Tieralter (Wochen)'];
    groupAgeChartData: (string | number)[][] = [];
    groupAgeChartHasData = false;
    showAllGroupAgeGroups = false;
    groupAgeUnit: GroupAgeUnit = 'weeks';
    groupAgeChartOptions: any = {
        legend: { position: 'none' },
        tooltip: { trigger: 'focus' },
        height: 390,
        chartArea: { left: 188, top: 26, width: '72%', height: '72%' },
        hAxis: {
            minValue: 0,
            format: '#,###',
            viewWindowMode: 'pretty',
            textStyle: { fontSize: 10, color: '#6b7280' },
            baselineColor: '#9ca3af',
            gridlines: { color: '#e5e7eb', count: 6 },
            minorGridlines: { color: '#f3f4f6' },
        },
        vAxis: {
            textStyle: { fontSize: 11, color: '#374151' },
        },
        bar: { groupWidth: '62%' },
        colors: ['#0ea5e9'],
        backgroundColor: 'transparent',
    };
    private activeGroupRecords: any[] = [];
    private priceChartRecords: any[] = [];
    private weightTimelineRecords: any[] = [];
    private historyGroupRecords: any[] = [];
    readonly weightTimelineChartType = ChartType.BarChart;
    weightTimelineChartColumns: any[] = [
        { type: 'string', id: 'Gruppe' },
        { type: 'number', id: 'Standort 1' },
        { type: 'string', role: 'tooltip', p: { html: true } },
        { type: 'string', role: 'style' },
    ];
    weightTimelineChartData: (string | number | Date | null)[][] = [];
    weightTimelineChartHasData = false;
    weightTimelineChartOptions: any = {
        tooltip: { trigger: 'focus', isHtml: true },
        annotations: {
            alwaysOutside: false,
            textStyle: {
                fontSize: 11,
                color: '#ffffff',
                auraColor: 'none',
            },
        },
        height: 420,
        hAxis: {
            format: '#,##0',
            textStyle: { fontSize: 10, color: '#6b7280' },
            gridlines: { color: '#d1d5db' },
            minorGridlines: { color: '#e5e7eb' },
            title: 'Gewicht (kg)',
        },
        vAxis: {
            textStyle: { fontSize: 10, color: '#374151' },
        },
        bars: 'horizontal',
        isStacked: true,
        legend: { position: 'none' },
        backgroundColor: 'transparent',
        bar: { groupWidth: '70%' },
    };

    constructor(
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private api: KieneBackendApiService,
        private localStorageService: LocalStorageService,
        private router: Router,
        private route: ActivatedRoute,
        private messageService: MessageService,
        private gruppenDashboardImportRefresh: GruppenDashboardImportRefreshService,
    ) { }

    ngOnInit(): void {
        this.currentClient = this.localStorageService.getCurrentClient();
        if (this.localStorageService.isCurrentClientSchwein()) {
            void this.router.navigate(['/gruppen/aktuelle']);
            return;
        }
        this.loadDashboard();

        this.importRefreshSubscription = this.gruppenDashboardImportRefresh.onImportCompleted.subscribe(tierlisteId => {
            this.onImportTierlisteCompleted(tierlisteId);
        });

        this.routeSubscription = this.route.queryParamMap.subscribe(queryParams => {
            const tierlisteId = Number(queryParams.get('tierlisteId') || 0);
            if (tierlisteId > 0 && tierlisteId !== this.openedTierlisteIdFromRoute) {
                this.pendingTierlisteIdFromRoute = tierlisteId;
                this.tryOpenPendingTierlisteFromRoute();
            }
        });

        this.watchClientSubscription = this.localStorageService.watchCurrentClient().subscribe(client => {
            this.currentClient = client;
            if (this.localStorageService.isCurrentClientSchwein()) {
                void this.router.navigate(['/gruppen/aktuelle']);
                return;
            }
            this.loadDashboard();
        });
    }

    ngOnDestroy(): void {
        this.watchClientSubscription?.unsubscribe();
        this.importRefreshSubscription?.unsubscribe();
        this.routeSubscription?.unsubscribe();
        this.stopChartsResizeObserver();
    }

    get clientLabel(): string {
        if (!this.currentClient) {
            return 'Alle Betriebe';
        }
        const base = this.currentClient?.bezeichnung || 'Betrieb';
        return this.currentClient?.zusatz ? `${base} - ${this.currentClient.zusatz}` : base;
    }

    get showMoreImportTierlistenButton(): boolean {
        return !this.importTierlistenExpanded && this.importTierlistenHasMore;
    }

    openBereitZumImportieren(): void {
        this.router.navigate(['/gruppen/tierlisten'], { queryParams: { tab: 1 } }).then();
    }

    onImportTierlisteChanged(): void {
        this.reloadImportTierlistenPanel();
    }

    onLetzteAktionenRefresh(): void {
        this.loadImportTierlisten(this.importTierlistenExpanded);
    }

    onImportTierlisteCompleted(tierlisteId: number): void {
        if (tierlisteId > 0) {
            this.importTierlisten = this.importTierlisten.filter(item => item.tierliste_id !== tierlisteId);
            this.importTierlistenHasMore = this.importTierlistenExpanded
                ? false
                : this.importTierlisten.length > this.previewLimit;
        }
        this.reloadImportTierlistenPanel();
    }

    private reloadImportTierlistenPanel(): void {
        this.loadImportTierlisten(this.importTierlistenExpanded);
        this.letzteAktionenPanel?.onTierlisteMenuChanged();
    }

    loadAllImportTierlisten(): void {
        if (this.importTierlistenState.loading || this.importTierlistenExpanded) {
            return;
        }
        this.importTierlistenExpanded = true;
        this.loadImportTierlisten(true);
    }

    openImportTierlisteRow(tierliste: DashboardTierlisteItem): void {
        if (!tierliste?.tierliste_id || Number(tierliste.status_id) !== 162) {
            return;
        }
        if (!this.localStorageService.hasPermission(PERMISSION_IMPORTIEREN)) {
            this.openTierlistenTabById(1, tierliste);
            return;
        }
        const menu = this.importTierlisteMenus?.find(item => item.tierlisteId === tierliste.tierliste_id);
        if (menu) {
            menu.openPrimaryAction();
            return;
        }
        this.openTierlistenTabById(1, tierliste);
    }

    openTierlistenTabById(tab: 0 | 1, tierliste: DashboardTierlisteItem): void {
        if (!tierliste?.tierliste_id) {
            return;
        }
        this.router
            .navigate(['/gruppen/tierlisten'], {
                queryParams: { tab, tierlisteId: tierliste.tierliste_id },
            })
            .then();
    }

    getStatusLabel(statusId: number): string {
        if (statusId === 161) {
            return 'Offen';
        }
        if (statusId === 162) {
            return 'Abgeschlossen';
        }
        if (statusId === 163) {
            return 'Importiert';
        }
        return 'Unbekannt';
    }

    getStatusChipClass(statusId: number): string {
        if (statusId === 161) {
            return 'item-chip--status-161';
        }
        if (statusId === 162) {
            return 'item-chip--status-162';
        }
        if (statusId === 163) {
            return 'item-chip--status-163';
        }
        return '';
    }

    onChartGroupingChange(grouping: DashboardChartGrouping): void {
        if (this.chartGrouping === grouping) {
            return;
        }
        this.chartGrouping = grouping;
        this.rebuildChartData();
    }

    getChartWindowLabel(): string {
        if (this.chartGrouping === 'day') {
            return 'Tage';
        }
        if (this.chartGrouping === 'month') {
            return 'Monate';
        }
        return 'Wochen';
    }

    onDashboardTabChange(index: number): void {
        this.isChartsTabActive = index === 1;
        if (!this.isChartsTabActive) {
            this.stopChartsResizeObserver();
            return;
        }

        this.refreshChartsAfterTabOpen();
        setTimeout(() => this.startChartsResizeObserver(), 0);
    }

    toggleChartSeries(series: DashboardSeriesKey): void {
        const activeSeriesCount = Object.values(this.chartSeriesVisibility).filter(Boolean).length;
        if (this.chartSeriesVisibility[series] && activeSeriesCount === 1) {
            return;
        }
        this.chartSeriesVisibility[series] = !this.chartSeriesVisibility[series];
        this.rebuildChartData();
    }

    isChartSeriesVisible(series: DashboardSeriesKey): boolean {
        return !!this.chartSeriesVisibility[series];
    }

    togglePriceChartSeries(series: DashboardPriceSeriesKey): void {
        const activeSeriesCount = Object.values(this.priceChartSeriesVisibility).filter(Boolean).length;
        if (this.priceChartSeriesVisibility[series] && activeSeriesCount === 1) {
            return;
        }
        this.priceChartSeriesVisibility[series] = !this.priceChartSeriesVisibility[series];
        this.rebuildPriceChartData();
    }

    isPriceChartSeriesVisible(series: DashboardPriceSeriesKey): boolean {
        return !!this.priceChartSeriesVisibility[series];
    }

    setPriceDateFilter(filter: PriceDateFilter): void {
        if (this.priceDateFilter === filter) {
            return;
        }
        this.priceDateFilter = filter;
        this.loadPriceChartRecords();
    }

    setWeightDateFilter(filter: WeightDateFilter): void {
        if (this.weightDateFilter === filter) {
            return;
        }
        this.weightDateFilter = filter;
        this.loadWeightTimelineChartRecords();
    }

    toggleGroupAgeGroupScope(): void {
        this.showAllGroupAgeGroups = !this.showAllGroupAgeGroups;
        this.rebuildGroupAgeChartData();
    }

    setGroupAgeUnit(unit: GroupAgeUnit): void {
        if (this.groupAgeUnit === unit) {
            return;
        }
        this.groupAgeUnit = unit;
        this.rebuildGroupAgeChartData();
    }

    private loadDashboard(): void {
        this.importTierlistenExpanded = false;
        this.importTierlistenHasMore = false;
        this.loadTiergruppenSummary();
        this.loadHistoryTiergruppenSummary();
        this.loadImportTierlisten(false);
        this.loadPriceChartRecords();
        this.loadWeightTimelineChartRecords();
    }

    private loadTiergruppenSummary(): void {
        let params = new HttpParams();
        if (this.currentClient?.kunde_id) {
            params = params.set('kunde_id', this.currentClient.kunde_id);
        }

        this.api.get(this.apiBaseUrl + 'gruppe/read_akt_kundenportal.php', params).subscribe({
            next: response => {
                const records = this.normalizeRecords(response);
                this.activeGroupRecords = records;
                this.rebuildChartData();
                this.rebuildPriceChartData();
                this.rebuildGroupAgeChartData();
            },
            error: () => {
                this.activeGroupRecords = [];
                this.rebuildChartData();
                this.rebuildPriceChartData();
                this.rebuildGroupAgeChartData();
            },
        });
    }

    private loadHistoryTiergruppenSummary(): void {
        let params = new HttpParams();
        if (this.currentClient?.kunde_id) {
            params = params.set('kunde_id', this.currentClient.kunde_id);
        }
        this.api.get(this.apiBaseUrl + 'gruppe/read_hist_kundenportal.php', params).subscribe({
            next: response => {
                const records = this.normalizeRecords(response);
                this.historyGroupRecords = records;
                this.rebuildChartData();
                this.rebuildPriceChartData();
            },
            error: () => {
                this.historyGroupRecords = [];
                this.rebuildChartData();
                this.rebuildPriceChartData();
            },
        });
    }

    private loadImportTierlisten(loadAll: boolean): void {
        this.importTierlistenState = { loading: true, error: null };
        const endpoint = this.apiBaseUrl + 'tierliste/read_einzustallen.php';
        let requestParams = new HttpParams();
        if (this.currentClient?.kunde_id) {
            requestParams = requestParams.set('kunde_id', this.currentClient.kunde_id);
        }
        requestParams = requestParams.set('orderby', 'IFNULL(t.editiert,t.erstellt) desc, t.tierliste_id desc');
        if (!loadAll) {
            requestParams = requestParams.set('limit', String(this.previewLimit + 1));
        }

        this.api.get(endpoint, requestParams).subscribe({
            next: previewResponse => {
                const tierlisten = this.normalizeRecords(previewResponse)
                    .map(item => this.mapTierlisteItem(item))
                    .sort((a, b) => this.compareTierlistenByMostActual(a, b));
                if (loadAll) {
                    this.importTierlisten = tierlisten;
                    this.importTierlistenHasMore = false;
                } else {
                    this.importTierlistenHasMore = tierlisten.length > this.previewLimit;
                    this.importTierlisten = this.importTierlistenHasMore
                        ? tierlisten.slice(0, this.previewLimit)
                        : tierlisten;
                }
                this.importTierlistenExpanded = loadAll;
                this.importTierlistenState = { loading: false, error: null };
                this.tryOpenPendingTierlisteFromRoute();
            },
            error: () => {
                this.importTierlisten = [];
                this.importTierlistenExpanded = false;
                this.importTierlistenHasMore = false;
                this.importTierlistenState = {
                    loading: false,
                    error: 'Importierbare Tierlisten konnten nicht geladen werden.',
                };
            },
        });
    }

    private tryOpenPendingTierlisteFromRoute(): void {
        const tierlisteId = this.pendingTierlisteIdFromRoute;
        if (!tierlisteId || tierlisteId === this.openedTierlisteIdFromRoute || this.importTierlistenState.loading) {
            return;
        }

        const fromList = this.importTierlisten.find(item => item.tierliste_id === tierlisteId);
        if (fromList) {
            this.openTierlisteFromRoute(fromList, tierlisteId);
            return;
        }

        if (this.importTierlistenHasMore && !this.importTierlistenExpanded) {
            this.importTierlistenExpanded = true;
            this.loadImportTierlisten(true);
            return;
        }

        this.finishTierlisteRouteWithoutAction(
            tierlisteId,
            'Diese Ausstallliste ist nicht mehr zum Import bereit.',
        );
    }

    private openTierlisteFromRoute(tierliste: DashboardTierlisteItem, tierlisteId: number): void {
        this.openedTierlisteIdFromRoute = tierlisteId;
        this.pendingTierlisteIdFromRoute = null;
        setTimeout(() => {
            this.openImportTierlisteRow(tierliste);
            this.clearTierlisteRouteQueryParam();
        });
    }

    private finishTierlisteRouteWithoutAction(tierlisteId: number, infoMessage?: string): void {
        this.openedTierlisteIdFromRoute = tierlisteId;
        this.pendingTierlisteIdFromRoute = null;
        this.clearTierlisteRouteQueryParam();
        if (infoMessage) {
            this.messageService.infoMessage(infoMessage);
        }
    }

    private clearTierlisteRouteQueryParam(): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tierlisteId: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    private mapTierlisteItem(item: any): DashboardTierlisteItem {
        return {
            tierliste_id: Number(item?.tierliste_id || 0),
            datum_abgang: item?.datum_abgang,
            erstellt: item?.erstellt || null,
            editiert: item?.editiert || null,
            gruppe_abgang: item?.gruppe_abgang || '-',
            kunden_abgang: item?.kunden_abgang || '-',
            kunde_einstallen: item?.kunde_einstallen || '',
            kunde_zielbetrieb: item?.kunde_zielbetrieb || '',
            anzahl_tiere: Number(item?.anzahl_tiere || 0),
            status_id: Number(item?.status_id || 0),
            status: item?.status || '',
        };
    }

    showImportTierlisteRoute(tierliste: DashboardTierlisteItem): boolean {
        return shouldShowImportTierlisteZielRoute(tierliste);
    }

    getImportTierlisteZielName(tierliste: DashboardTierlisteItem): string {
        return getTierlisteZielbetriebAnzeigeName(tierliste);
    }

    private normalizeRecords(response: any): any[] {
        if (Array.isArray(response)) {
            return response;
        }
        if (Array.isArray(response?.records)) {
            return response.records;
        }
        return [];
    }

    private compareTierlistenByMostActual(a: DashboardTierlisteItem, b: DashboardTierlisteItem): number {
        const aTs = this.getTierlisteSortTimestamp(a);
        const bTs = this.getTierlisteSortTimestamp(b);
        if (aTs !== bTs) {
            return bTs - aTs;
        }
        return Number(b?.tierliste_id || 0) - Number(a?.tierliste_id || 0);
    }

    private getTierlisteSortTimestamp(item: DashboardTierlisteItem): number {
        const date = this.asDate(item?.editiert) || this.asDate(item?.erstellt) || this.asDate(item?.datum_abgang);
        return date?.getTime() || 0;
    }

    private rebuildChartData(): void {
        const periodStarts = this.buildPeriodStarts();
        const eingestallteEvents = this.activeGroupRecords.map(record => ({
            date: this.asDate(record?.einstalldatum_von),
            value: Number(record?.anzahl_tiere_gesamt || record?.anzahl_tiere_aktuell || 0),
        }));
        const eingestallteSeries = this.aggregateSeries(
            eingestallteEvents,
            periodStarts,
        );
        const ausgestallteEvents = this.historyGroupRecords.map(record => ({
            date: this.asDate(record?.abgangsdatum_bis),
            value: Number(record?.anzahl_tiere_gesamt || 0),
        }));
        const ausgestallteSeries = this.aggregateSeries(
            ausgestallteEvents,
            periodStarts,
        );
        const toteEvents = this.historyGroupRecords.map(record => ({
            date: this.asDate(record?.abgangsdatum_bis),
            value: Number(record?.anzahl_tote || 0),
        }));
        const toteSeries = this.aggregateSeries(
            toteEvents,
            periodStarts,
        );

        const hasDataInSelectedPeriod =
            this.hasSeriesDataInSelectedPeriod(eingestallteEvents, periodStarts) ||
            this.hasSeriesDataInSelectedPeriod(ausgestallteEvents, periodStarts) ||
            this.hasSeriesDataInSelectedPeriod(toteEvents, periodStarts);

        const seriesValuesByKey: Record<DashboardSeriesKey, number> = {
            eingestallte: 0,
            ausgestallte: 0,
            tot: 0,
        };
        const visibleSeries = this.chartSeriesMeta.filter(series => this.chartSeriesVisibility[series.key]);
        let maxVisibleValue = 0;
        let hasNonZeroDataInSelectedPeriod = false;

        this.chartColumns = ['Zeitraum', ...visibleSeries.map(series => series.label)];
        this.chartOptions = {
            ...this.chartOptions,
            colors: visibleSeries.map(series => series.color),
            hAxis: this.buildMovementHorizontalAxisOptions(),
        };

        this.chartData = periodStarts.map(periodStart => {
            const key = this.getPeriodKey(periodStart);
            seriesValuesByKey.eingestallte = eingestallteSeries.get(key) || 0;
            seriesValuesByKey.ausgestallte = ausgestallteSeries.get(key) || 0;
            seriesValuesByKey.tot = toteSeries.get(key) || 0;
            visibleSeries.forEach(series => {
                maxVisibleValue = Math.max(maxVisibleValue, seriesValuesByKey[series.key]);
                if (seriesValuesByKey[series.key] > 0) {
                    hasNonZeroDataInSelectedPeriod = true;
                }
            });

            return [this.formatPeriodLabel(periodStart), ...visibleSeries.map(series => seriesValuesByKey[series.key])];
        });

        const yAxisPaddingFactor = 1.15;
        const yAxisMinTopGap = 1;
        const yAxisMax = maxVisibleValue > 0
            ? Math.ceil(maxVisibleValue * yAxisPaddingFactor + yAxisMinTopGap)
            : 1;
        const axisTicks = this.buildCountAxisTicks(yAxisMax);
        const movementChartArea = this.buildMovementChartArea(yAxisMax);

        this.chartOptions = {
            ...this.chartOptions,
            chartArea: movementChartArea,
            vAxis: {
                ...this.chartOptions.vAxis,
                minValue: 0,
                viewWindowMode: 'explicit',
                viewWindow: {
                    min: 0,
                    max: yAxisMax,
                },
                ticks: axisTicks,
            },
        };
        this.chartHasData = hasDataInSelectedPeriod;
    }

    private rebuildPriceChartData(): void {
        const events: DashboardPriceByDateEvent[] = this.priceChartRecords.map(record => ({
            date: this.asDate(record?.kp_einstalldatum || record?.einstalldatum_notnull || record?.hit_einstalldatum),
            gruppeId: this.toFiniteNumber(record?.gruppe_id),
            gruppeName: String(record?.gruppe || '').trim() || 'Unbekannte Gruppe',
            einstallpreis: this.toFiniteNumber(record?.kp_einstallpreis),
            abgangspreis: this.toFiniteNumber(record?.kp_abgangspreis),
            einstallmwst: this.toFiniteNumber(
                record?.kp_einstallmwst ?? this.calculateMwst(
                    this.toFiniteNumber(record?.kp_einstallpreis),
                    this.toFiniteNumber(record?.kp_einstallmwstsatz),
                ),
            ),
            abgangmwst: this.toFiniteNumber(
                record?.kp_abgangmwst ?? this.calculateMwst(
                    this.toFiniteNumber(record?.kp_abgangspreis),
                    // backend naming varies; support both spellings
                    this.toFiniteNumber(record?.kp_abgangsmwstsatz ?? record?.kp_abgangmwstsatz),
                ),
            ),
        }));
        const visibleSeries = this.priceChartSeriesMeta.filter(series => this.priceChartSeriesVisibility[series.key]);
        const seriesLabelByKey = new Map(this.priceChartSeriesMeta.map(series => [series.key, series.label]));
        const groupedByDate = new Map<string, { date: Date; groups: Map<number, DashboardPriceGroupAggregate> }>();

        events.forEach(event => {
            if (!event.date || Number.isNaN(event.date.getTime())) {
                return;
            }
            const normalizedDate = new Date(event.date.getFullYear(), event.date.getMonth(), event.date.getDate());
            const dateKey = this.getDateKey(normalizedDate);
            if (!groupedByDate.has(dateKey)) {
                groupedByDate.set(dateKey, { date: normalizedDate, groups: new Map<number, DashboardPriceGroupAggregate>() });
            }
            const dateEntry = groupedByDate.get(dateKey);
            if (!dateEntry) {
                return;
            }
            const existingGroup = dateEntry.groups.get(event.gruppeId);
            if (!existingGroup) {
                dateEntry.groups.set(event.gruppeId, {
                    gruppeId: event.gruppeId,
                    gruppeName: event.gruppeName,
                    einstallpreis: 0,
                    abgangspreis: 0,
                    einstallmwst: 0,
                    abgangmwst: 0,
                });
            }
            const groupEntry = dateEntry.groups.get(event.gruppeId);
            if (!groupEntry) {
                return;
            }
            groupEntry.einstallpreis += event.einstallpreis;
            groupEntry.abgangspreis += event.abgangspreis;
            groupEntry.einstallmwst += event.einstallmwst;
            groupEntry.abgangmwst += event.abgangmwst;
        });

        let minVisibleValue = 0;
        let maxVisibleValue = 0;
        let hasVisibleData = false;
        const sortedEntries = Array.from(groupedByDate.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
        const limitedEntries = sortedEntries.slice(-this.priceChartMaxElements);

        const allGroups: DashboardPriceGroupAggregate[] = [];
        limitedEntries.forEach(entry => {
            entry.groups.forEach(group => {
                allGroups.push(group);
            });
        });
        const uniqueGroups = new Map<number, DashboardPriceGroupAggregate>();
        allGroups
            .sort((a, b) => a.gruppeName.localeCompare(b.gruppeName, 'de-DE') || a.gruppeId - b.gruppeId)
            .forEach(group => {
                if (!uniqueGroups.has(group.gruppeId)) {
                    uniqueGroups.set(group.gruppeId, group);
                }
            });
        const orderedGroups: DashboardPriceGroupAggregate[] = Array.from(uniqueGroups.values());
        const dynamicColumns: any[] = ['Datum'];
        orderedGroups.forEach(group => {
            dynamicColumns.push({ type: 'number', label: group.gruppeName });
            dynamicColumns.push({ type: 'string', role: 'style' });
            dynamicColumns.push({ type: 'string', role: 'tooltip', p: { html: true } });
        });

        this.priceChartColumns = dynamicColumns;
        this.priceChartOptions = {
            ...this.priceChartOptions,
            tooltip: {
                ...this.priceChartOptions.tooltip,
                trigger: 'focus',
                isHtml: true,
            },
            focusTarget: 'datum',
            colors: ['#1d4ed8'],
            hAxis: this.buildPriceHorizontalAxisOptions(visibleSeries.length),
            isStacked: true,
        };

        this.priceChartData = [];
        limitedEntries.forEach(entry => {
            const dateLabel = this.formatDateLabel(entry.date);
            visibleSeries.forEach((series, seriesIndex) => {
                const seriesLabel = seriesLabelByKey.get(series.key) || series.label;
                const rowLabel = seriesIndex === 0 ? dateLabel : '';
                const row: (string | number | null)[] = [rowLabel];

                orderedGroups.forEach((group, groupIndex) => {
                    const currentGroup = entry.groups.get(group.gruppeId);
                    const hasGroupData = this.hasPriceAggregateData(currentGroup);
                    const value = hasGroupData && currentGroup ? this.toFiniteNumber(currentGroup[series.key]) : null;
                    if (typeof value === 'number' && Number.isFinite(value)) {
                        if (!hasVisibleData) {
                            minVisibleValue = value;
                            maxVisibleValue = value;
                            hasVisibleData = true;
                        } else {
                            minVisibleValue = Math.min(minVisibleValue, value);
                            maxVisibleValue = Math.max(maxVisibleValue, value);
                        }
                    }
                    const safeGroupName = this.escapeHtml(group.gruppeName);
                    const safeSeriesLabel = this.escapeHtml(seriesLabel);
                    const pointColor = this.getPriceSeriesColor(series.key, groupIndex);
                    const tooltip = value === null
                        ? null
                        : `<div style="padding:8px 10px;"><div>Gruppe: ${safeGroupName}</div><div>${safeSeriesLabel}: ${value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div><div>Datum: ${dateLabel}</div></div>`;
                    row.push(value);
                    row.push(value === null ? null : `color: ${pointColor}`);
                    row.push(tooltip);
                });

                this.priceChartData.push(row);
            });

            // Keep visual spacing between date blocks.
            const spacerRow: (string | number | null)[] = [''];
            orderedGroups.forEach(() => {
                spacerRow.push(null);
                spacerRow.push(null);
                spacerRow.push(null);
            });
            this.priceChartData.push(spacerRow);
        });

        const yAxisPaddingFactor = 0.15;
        const yAxisMinimalSpan = 1;
        const paddedMin = hasVisibleData
            ? minVisibleValue - Math.max(Math.abs(minVisibleValue) * yAxisPaddingFactor, yAxisMinimalSpan)
            : 0;
        const paddedMax = hasVisibleData
            ? maxVisibleValue + Math.max(Math.abs(maxVisibleValue) * yAxisPaddingFactor, yAxisMinimalSpan)
            : 1;
        const yAxisMin = 0;
        const yAxisMax = Math.max(paddedMax, yAxisMin + yAxisMinimalSpan);

        this.priceChartOptions = {
            ...this.priceChartOptions,
            vAxis: {
                ...this.priceChartOptions.vAxis,
                viewWindowMode: 'explicit',
                viewWindow: {
                    min: yAxisMin,
                    max: yAxisMax,
                },
            },
        };
        this.priceChartHasData = this.priceChartData.length > 0;
    }

    private rebuildGroupAgeChartData(): void {
        const sortedByAnimals = this.activeGroupRecords
            .slice()
            .sort((a, b) => Number(b?.anzahl_tiere_aktuell || 0) - Number(a?.anzahl_tiere_aktuell || 0));
        const sourceGroupsBase = this.showAllGroupAgeGroups
            ? sortedByAnimals
            : sortedByAnimals.slice(0, this.previewLimit);
        const sourceGroups = sourceGroupsBase.filter(group => this.toFiniteNumber(group?.standard) <= 0);

        const rows = sourceGroups
            .map(group => {
                const groupName = String(group?.bezeichnung || '').trim();
                const ageValue = this.groupAgeUnit === 'days'
                    ? this.toFiniteNumber(group?.alter_tage)
                    : this.toFiniteNumber(group?.alter_wochen);
                return [groupName || 'Unbenannte Gruppe', Math.max(0, Math.round(ageValue))] as [string, number];
            })
            .filter(row => !!row[0]);

        const maxTieralter = rows.reduce((max, [, tieralter]) => Math.max(max, tieralter), 0);
        const axisMax = Math.max(1, Math.ceil(maxTieralter * 1.15 + 1));
        const baseHeight = 390;
        const rowHeight = 36;
        const maxExpandedHeight = 1400;
        const dynamicHeight = this.showAllGroupAgeGroups
            ? Math.min(maxExpandedHeight, Math.max(baseHeight, rows.length * rowHeight + 110))
            : baseHeight;

        this.groupAgeChartData = rows;
        this.groupAgeChartColumns = ['Gruppe', this.groupAgeUnit === 'days' ? 'Tieralter (Tage)' : 'Tieralter (Wochen)'];
        this.groupAgeChartOptions = {
            ...this.groupAgeChartOptions,
            height: dynamicHeight,
            chartArea: {
                ...this.groupAgeChartOptions.chartArea,
                height: this.showAllGroupAgeGroups ? '82%' : '72%',
            },
            hAxis: {
                ...this.groupAgeChartOptions.hAxis,
                minValue: 0,
                viewWindowMode: 'explicit',
                viewWindow: {
                    min: 0,
                    max: axisMax,
                },
            },
        };
        this.groupAgeChartHasData = rows.length > 0;
    }

    private escapeHtml(value: string): string {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    private getPriceSeriesColor(seriesKey: DashboardPriceSeriesKey, groupIndex: number): string {
        const palettes: Record<DashboardPriceSeriesKey, string[]> = {
            einstallpreis: ['#1d4ed8', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd'],
            abgangspreis: ['#15803d', '#16a34a', '#22c55e', '#4ade80', '#86efac'],
            einstallmwst: ['#c4b5fd', '#d8b4fe', '#ddd6fe', '#e9d5ff', '#f3e8ff'],
            abgangmwst: ['#c4b5fd', '#d8b4fe', '#ddd6fe', '#e9d5ff', '#f3e8ff'],
        };
        const palette = palettes[seriesKey];
        return palette[groupIndex % palette.length];
    }

    private refreshChartsAfterTabOpen(): void {
        this.redrawCharts();
        // Keep one delayed redraw for tab animation completion.
        setTimeout(() => this.redrawCharts(), 250);
    }

    private redrawCharts(): void {
        this.chartData = [...this.chartData];
        this.chartColumns = [...this.chartColumns];
        this.chartOptions = { ...this.chartOptions };
        this.priceChartData = [...this.priceChartData];
        this.priceChartColumns = [...this.priceChartColumns];
        this.priceChartOptions = { ...this.priceChartOptions };
        this.groupAgeChartData = [...this.groupAgeChartData];
        this.groupAgeChartColumns = [...this.groupAgeChartColumns];
        this.groupAgeChartOptions = { ...this.groupAgeChartOptions };
        this.weightTimelineChartData = [...this.weightTimelineChartData];
        this.weightTimelineChartColumns = [...this.weightTimelineChartColumns];
        this.weightTimelineChartOptions = { ...this.weightTimelineChartOptions };
        window.dispatchEvent(new Event('resize'));
    }

    private startChartsResizeObserver(): void {
        const host = this.chartsTabContentRef?.nativeElement;
        if (!host || typeof ResizeObserver === 'undefined') {
            return;
        }

        this.stopChartsResizeObserver();
        this.chartsResizeObserver = new ResizeObserver(entries => {
            const entry = entries[0];
            if (!entry || entry.contentRect.width <= 0 || !this.isChartsTabActive) {
                return;
            }
            this.redrawCharts();
        });
        this.chartsResizeObserver.observe(host);
    }

    private stopChartsResizeObserver(): void {
        this.chartsResizeObserver?.disconnect();
        this.chartsResizeObserver = null;
    }

    private buildPeriodStarts(): Date[] {
        const starts: Date[] = [];
        const now = new Date();
        const currentPeriodStart = this.getPeriodStart(now);
        for (let i = this.chartWindowSize - 1; i >= 0; i--) {
            starts.push(this.addPeriodOffset(currentPeriodStart, -i));
        }
        return starts;
    }

    private aggregateSeries(events: DashboardSeriesEvent[], periodStarts: Date[]): Map<string, number> {
        const values = new Map<string, number>();
        const allowedKeys = new Set(periodStarts.map(periodStart => this.getPeriodKey(periodStart)));
        periodStarts.forEach(periodStart => values.set(this.getPeriodKey(periodStart), 0));

        events.forEach(event => {
            if (!event.date || Number.isNaN(event.date.getTime())) {
                return;
            }
            const periodKey = this.getPeriodKey(this.getPeriodStart(event.date));
            if (!allowedKeys.has(periodKey)) {
                return;
            }
            const currentValue = values.get(periodKey) || 0;
            values.set(periodKey, currentValue + (event.value || 0));
        });
        return values;
    }

    private getPeriodStart(date: Date): Date {
        const normalized = new Date(date);
        normalized.setHours(0, 0, 0, 0);

        if (this.chartGrouping === 'day') {
            return normalized;
        }
        if (this.chartGrouping === 'month') {
            return new Date(normalized.getFullYear(), normalized.getMonth(), 1);
        }

        const day = normalized.getDay();
        const diffToMonday = (day + 6) % 7;
        normalized.setDate(normalized.getDate() - diffToMonday);
        return normalized;
    }

    private addPeriodOffset(date: Date, offset: number): Date {
        const shifted = new Date(date);
        if (this.chartGrouping === 'day') {
            shifted.setDate(shifted.getDate() + offset);
            return shifted;
        }
        if (this.chartGrouping === 'month') {
            return new Date(shifted.getFullYear(), shifted.getMonth() + offset, 1);
        }
        shifted.setDate(shifted.getDate() + offset * 7);
        return shifted;
    }

    private getPeriodKey(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        if (this.chartGrouping === 'month') {
            return `${year}-${month}`;
        }
        return `${year}-${month}-${day}`;
    }

    private getDateKey(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private formatPeriodLabel(date: Date): string {
        if (this.chartGrouping === 'day') {
            return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
        }
        if (this.chartGrouping === 'month') {
            return date.toLocaleDateString('de-DE', { month: '2-digit', year: '2-digit' });
        }
        const isoWeek = this.getIsoWeek(date);
        return `KW ${String(isoWeek.week).padStart(2, '0')}`;
    }

    private getIsoWeek(date: Date): { week: number; year: number } {
        const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNumber = target.getUTCDay() || 7;
        target.setUTCDate(target.getUTCDate() + 4 - dayNumber);
        const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
        return {
            week: Math.ceil((((target.getTime() - yearStart.getTime()) / 86400000) + 1) / 7),
            year: target.getUTCFullYear(),
        };
    }

    private asDate(value: any): Date | null {
        if (!value) {
            return null;
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return null;
        }
        return date;
    }

    private buildMovementHorizontalAxisOptions(): any {
        return this.buildHorizontalAxisOptionsFromBase(this.chartOptions?.hAxis);
    }

    private buildCountAxisTicks(yAxisMax: number): number[] {
        if (yAxisMax <= 1) {
            return [0, 1];
        }

        const steps = 5;
        const stepSize = Math.max(1, Math.ceil(yAxisMax / steps));
        const ticks: number[] = [0];
        for (let value = stepSize; value < yAxisMax; value += stepSize) {
            ticks.push(value);
        }
        if (ticks[ticks.length - 1] !== yAxisMax) {
            ticks.push(yAxisMax);
        }
        return ticks;
    }

    private buildMovementChartArea(yAxisMax: number): any {
        const formattedMax = yAxisMax.toLocaleString('de-DE');
        // Wider numbers in weekly aggregates need extra left axis space.
        const left = Math.min(168, Math.max(96, 72 + (formattedMax.length * 9)));
        return {
            ...this.chartOptions?.chartArea,
            left,
        };
    }

    private buildPriceHorizontalAxisOptions(visibleSeriesCount: number): any {
        const safeSeriesCount = Math.max(1, visibleSeriesCount);
        return {
            ...this.priceChartOptions?.hAxis,
            slantedText: false,
            slantedTextAngle: 0,
            // Keep a single date label per date block (first row + spacer row).
            showTextEvery: safeSeriesCount + 1,
        };
    }

    private buildHorizontalAxisOptionsFromBase(baseAxis: any): any {
        const isDenseDateLabels = this.chartGrouping === 'day' || this.chartGrouping === 'month';
        return {
            ...baseAxis,
            slantedText: isDenseDateLabels,
            slantedTextAngle: isDenseDateLabels ? 35 : 0,
            showTextEvery: 1,
        };
    }

    private hasSeriesDataInSelectedPeriod(events: DashboardSeriesEvent[], periodStarts: Date[]): boolean {
        const allowedKeys = new Set(periodStarts.map(periodStart => this.getPeriodKey(periodStart)));
        return events.some(event => {
            if (!event.date || Number.isNaN(event.date.getTime())) {
                return false;
            }
            const periodKey = this.getPeriodKey(this.getPeriodStart(event.date));
            return allowedKeys.has(periodKey);
        });
    }

    private formatDateLabel(date: Date): string {
        return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }

    private toFiniteNumber(value: any): number {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    private calculateMwst(price: number, mwstSatz: number): number {
        if (!Number.isFinite(price) || !Number.isFinite(mwstSatz) || mwstSatz <= 0) {
            return 0;
        }
        return (price * mwstSatz) / 100;
    }

    private hasPriceAggregateData(group?: DashboardPriceGroupAggregate): boolean {
        if (!group) {
            return false;
        }
        return (
            group.einstallpreis !== 0 ||
            group.abgangspreis !== 0 ||
            group.einstallmwst !== 0 ||
            group.abgangmwst !== 0
        );
    }

    private loadPriceChartRecords(): void {
        let params = new HttpParams();
        if (this.currentClient?.kunde_id) {
            params = params.set('kunde_id', this.currentClient.kunde_id);
        }
        params = params.set('date_filter', this.priceDateFilter);
        this.api.get(this.apiBaseUrl + 'gruppe_tier/read_preise_chart_kundenportal.php', params).subscribe({
            next: response => {
                this.priceChartRecords = this.normalizeRecords(response);
                this.rebuildPriceChartData();
            },
            error: () => {
                this.priceChartRecords = [];
                this.rebuildPriceChartData();
            },
        });
    }

    private rebuildWeightTimelineChartData(): void {
        const rows: DashboardWeightTimelineRecord[] = this.weightTimelineRecords
            .map(record => {
                const startWeight = this.toFiniteNumber(record?.einstallgewicht);
                const sourceEndWeight = this.toFiniteNumber(record?.endgewicht);
                const normalizedEndWeight = sourceEndWeight > 0 ? sourceEndWeight : startWeight;
                const minWeight = Math.min(startWeight, normalizedEndWeight);
                const maxWeight = Math.max(startWeight, normalizedEndWeight);
                const visualEndWeight = maxWeight > minWeight ? maxWeight : (minWeight + 1);
                const startDate = this.asDate(record?.standort_datum_von);
                const sourceEndDate = this.asDate(record?.standort_datum_bis);
                if (!startDate) {
                    return null;
                }
                let endDate = sourceEndDate ?? new Date();
                if (endDate.getTime() <= startDate.getTime()) {
                    endDate = new Date(startDate);
                    endDate.setDate(endDate.getDate() + 1);
                }
                return {
                    gruppeId: this.toFiniteNumber(record?.gruppe_id),
                    gruppeName: String(record?.gruppe || '').trim() || 'Unbenannte Gruppe',
                    standortName: String(record?.standort || '').trim(),
                    datumVon: String(record?.standort_datum_von || ''),
                    datumBis: String(record?.standort_datum_bis || ''),
                    startDate,
                    endDate,
                    startWeight: minWeight,
                    endWeight: visualEndWeight,
                };
            })
            .filter((row): row is DashboardWeightTimelineRecord => !!row)
            .filter(row => row.startWeight > 0 && row.endWeight > 0)
            .sort((a, b) =>
                a.gruppeName.localeCompare(b.gruppeName, 'de-DE') ||
                a.startDate.getTime() - b.startDate.getTime() ||
                a.gruppeId - b.gruppeId,
            );

        const grouped = new Map<number, DashboardWeightTimelineRecord[]>();
        rows.forEach(row => {
            if (!grouped.has(row.gruppeId)) {
                grouped.set(row.gruppeId, []);
            }
            grouped.get(row.gruppeId)?.push(row);
        });
        grouped.forEach(groupRows => {
            groupRows.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
        });

        const maxSegments = Array.from(grouped.values()).reduce((max, groupRows) => Math.max(max, groupRows.length), 0);
        const palette = ['#1d4ed8', '#16a34a', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];

        const dynamicColumns: any[] = [{ type: 'string', id: 'Gruppe' }];
        for (let i = 0; i < maxSegments; i++) {
            dynamicColumns.push({ type: 'number', id: `Standort ${i + 1}` });
            dynamicColumns.push({ type: 'string', role: 'annotation' });
            dynamicColumns.push({ type: 'string', role: 'tooltip', p: { html: true } });
            dynamicColumns.push({ type: 'string', role: 'style' });
        }
        this.weightTimelineChartColumns = dynamicColumns;

        const tableRows: (string | number | null)[][] = [];
        let maxCumulativeWeight = 0;
        Array.from(grouped.entries())
            .sort((a, b) => {
                const groupA = a[1][0]?.gruppeName || '';
                const groupB = b[1][0]?.gruppeName || '';
                return groupA.localeCompare(groupB, 'de-DE');
            })
            .forEach(([, groupRows]) => {
                const baseRow: (string | number | null)[] = [groupRows[0]?.gruppeName || 'Unbenannte Gruppe'];
                const baseline = groupRows.length > 0 ? groupRows[0].startWeight : 0;
                let previousEnd = 0;
                for (let i = 0; i < maxSegments; i++) {
                    const segment = groupRows[i];
                    if (!segment) {
                        baseRow.push(null);
                        baseRow.push(null);
                        baseRow.push(null);
                        baseRow.push(null);
                        continue;
                    }
                    const standortLabel = segment.standortName === 'Kein Standort' ? '' : segment.standortName;
                    const normalizedStart = Math.max(0, segment.startWeight - baseline);
                    const normalizedEndRaw = Math.max(0, segment.endWeight - baseline);
                    const normalizedEnd = Math.max(normalizedStart, normalizedEndRaw);
                    const segmentEnd = Math.max(previousEnd, normalizedEnd);
                    const segmentValue = Math.max(0, segmentEnd - previousEnd);
                    const datumVonLabel = this.formatTimelineDate(segment.datumVon);
                    const datumBisLabel = this.formatTimelineDate(segment.datumBis);
                    const tooltip =
                        `<div style="padding:8px 10px;">` +
                        `<div><strong>${this.escapeHtml(segment.gruppeName)}</strong></div>` +
                        `<div>Standort: ${this.escapeHtml(standortLabel || '-')}</div>` +
                        `<div>Von: ${this.escapeHtml(datumVonLabel)}</div>` +
                        `<div>Bis: ${this.escapeHtml(datumBisLabel)}</div>` +
                        `<div>Segment: ${segmentValue.toLocaleString('de-DE', { maximumFractionDigits: 2 })} kg</div>` +
                        `<div>Kumulativ (ab Start=0): ${segmentEnd.toLocaleString('de-DE', { maximumFractionDigits: 2 })} kg</div>` +
                        `<div>Absolut: ${segment.startWeight.toLocaleString('de-DE', { maximumFractionDigits: 2 })} - ${segment.endWeight.toLocaleString('de-DE', { maximumFractionDigits: 2 })} kg</div>` +
                        `</div>`;
                    const color = palette[i % palette.length];
                    baseRow.push(segmentValue);
                    baseRow.push(standortLabel);
                    baseRow.push(tooltip);
                    baseRow.push(`color: ${color}`);
                    previousEnd = segmentEnd;
                }
                maxCumulativeWeight = Math.max(maxCumulativeWeight, previousEnd);
                tableRows.push(baseRow);
            });
        // Force annotations to stay inside segment width.
        const axisSpan = Math.max(1, maxCumulativeWeight);
        for (let rowIndex = 0; rowIndex < tableRows.length; rowIndex++) {
            const row = tableRows[rowIndex];
            for (let segIndex = 0; segIndex < maxSegments; segIndex++) {
                const valueCol = 1 + (segIndex * 4);
                const annotationCol = valueCol + 1;
                const segmentValue = Number(row[valueCol] || 0);
                const rawLabel = String(row[annotationCol] || '').trim();
                row[annotationCol] = this.buildStandortAnnotationLabel(rawLabel, segmentValue, axisSpan);
            }
        }
        this.weightTimelineChartData = tableRows;
        const maxTimelineHeight = this.weightDateFilter === 'all' ? 4200 : 1800;
        const dynamicHeight = Math.min(maxTimelineHeight, Math.max(260, rows.length * 30 + 120));
        const safeMin = 0;
        const safeMax = Math.max(10, Math.ceil(maxCumulativeWeight + 5));
        const tickStep = Math.max(5, Math.ceil(safeMax / 6));
        const ticks: number[] = [];
        for (let v = safeMin; v <= safeMax; v += tickStep) {
            ticks.push(v);
        }
        if (ticks[ticks.length - 1] !== safeMax) {
            ticks.push(safeMax);
        }
        this.weightTimelineChartOptions = {
            ...this.weightTimelineChartOptions,
            height: dynamicHeight,
            hAxis: {
                ...this.weightTimelineChartOptions.hAxis,
                viewWindow: { min: safeMin, max: safeMax },
                ticks,
            },
        };
        this.weightTimelineChartHasData = rows.length > 0;
    }

    private loadWeightTimelineChartRecords(): void {
        let params = new HttpParams();
        if (this.currentClient?.kunde_id) {
            params = params.set('kunde_id', this.currentClient.kunde_id);
        }
        params = params.set('date_filter', this.weightDateFilter);
        this.api.get(this.apiBaseUrl + 'gruppe_tier/read_gewichte_timeline_kundenportal.php', params).subscribe({
            next: response => {
                this.weightTimelineRecords = this.normalizeRecords(response);
                this.rebuildWeightTimelineChartData();
            },
            error: () => {
                this.weightTimelineRecords = [];
                this.rebuildWeightTimelineChartData();
            },
        });
    }

    private formatTimelineDate(value: any): string {
        if (!value) {
            return '-';
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return '-';
        }
        return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }

    private buildStandortAnnotationLabel(standort: string, segmentValue: number, axisSpan: number): string {
        const label = (standort || '').trim();
        if (!label) {
            return '';
        }
        const safeAxisSpan = Math.max(1, axisSpan);
        const relativeShare = Math.max(0, Math.min(1, segmentValue / safeAxisSpan));
        // Approximation: full usable plot width is wider; allow more label chars.
        let estimatedMaxChars = Math.max(1, Math.floor(relativeShare * 140));
        // If a segment is visible, prefer at least a short readable prefix.
        if (segmentValue > 0 && estimatedMaxChars < 5) {
            estimatedMaxChars = 5;
        }
        if (label.length <= estimatedMaxChars) {
            return label;
        }
        if (estimatedMaxChars <= 1) {
            return `${label.slice(0, 1)}...`;
        }
        const prefixLength = Math.max(1, estimatedMaxChars - 3);
        return `${label.slice(0, prefixLength)}...`;
    }

}
